import { BellOutlined, DeleteOutlined, PlusOutlined, SendOutlined } from "@ant-design/icons";
import { Button, Input, InputNumber, Switch, Tabs, Typography, message, theme } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../config/serverApiConfig";
import "./Notify.css";

const { Title, Text } = Typography;
const STORE_KEY = "drkarwan.notify";

const SEND_TAB = {
  key: "send",
  label: "ناردنی گشتی",
};

const TABS = [
  {
    key: "photos",
    label: "پێشتر و دواتر",
    when: "after",
    eventLabel: "بارکردنی وێنە",
    hint: "ستاندارد بۆ هەموو چارەخوازەکان. دوای بارکردنی وێنەی پێشتر یان دواتر، لە چەند کاتێکدا ئاگادار دەکرێنەوە.",
  },
  {
    key: "lab",
    label: "ئەنجامی تاقیگە",
    when: "after",
    eventLabel: "تۆمارکردنی ئەنجام",
    hint: "ستاندارد بۆ هەموو چارەخوازەکان. دوای تۆمارکردنی ئەنجامی تاقیگە، لە چەند کاتێکدا ئاگادار دەکرێنەوە.",
  },
  {
    key: "history",
    label: "سەردانکردنی پێویست",
    when: "before",
    eventLabel: "سەردان",
    hint: "ستاندارد بۆ هەموو چارەخوازەکان. پێش کاتی سەردان، لە چەند کاتێکدا ئاگادار دەکرێنەوە.",
  },
  {
    key: "appointment",
    label: "چاوپێکەوتن",
    when: "before",
    eventLabel: "چاوپێکەوتن",
    hint: "ستاندارد بۆ هەموو چارەخوازەکان. پێش کاتی چاوپێکەوتن، لە چەند کاتێکدا ئاگادار دەکرێنەوە.",
  },
];

function defaultOffsets() {
  return [
    { hours: 8, minutes: 0 },
    { hours: 4, minutes: 0 },
    { hours: 2, minutes: 0 },
    { hours: 1, minutes: 0 },
  ];
}

function clamp(value, min, max, fallback) {
  const next = Number(value);
  if (Number.isNaN(next)) return fallback;
  return Math.min(max, Math.max(min, next));
}

function normalizeOffset(raw = {}) {
  return {
    hours: clamp(raw.hours, 0, 72, 1),
    minutes: clamp(raw.minutes, 0, 59, 0),
  };
}

function readOffsets(raw, when) {
  if (Array.isArray(raw[when]) && raw[when].length) {
    return raw[when].map(normalizeOffset);
  }
  if (Array.isArray(raw.offsets) && raw.offsets.length) {
    return raw.offsets.map(normalizeOffset);
  }
  if (raw.count && (raw.afterHours || raw.afterMinutes)) {
    const stepH = clamp(raw.afterHours, 0, 48, 0);
    const stepM = clamp(raw.afterMinutes, 0, 59, 0);
    const step = stepH * 60 + stepM;
    if (step > 0) {
      return Array.from({ length: clamp(raw.count, 1, 8, 1) }, (_, index) => {
        const total = step * (index + 1);
        return { hours: Math.floor(total / 60), minutes: total % 60 };
      });
    }
  }
  return defaultOffsets();
}

function normalizeRule(key, raw = {}) {
  const when = TABS.find((item) => item.key === key)?.when || "after";
  return {
    type: when,
    enabled: raw.enabled !== false,
    sendOnCreate: when === "before" && raw.sendOnCreate === true,
    [when]: readOffsets(raw, when),
  };
}

function emptyRule(key) {
  return normalizeRule(key, {});
}

function loadRules() {
  const next = {};
  TABS.forEach(({ key }) => {
    next[key] = emptyRule(key);
  });
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) || "{}");
    TABS.forEach(({ key }) => {
      next[key] = normalizeRule(key, raw[key]);
    });
  } catch {
    /* keep defaults */
  }
  return next;
}

function durationText(hours, minutes) {
  if (!hours && !minutes) return "یەکسەر";
  if (!hours) return `${minutes} خولەک`;
  if (!minutes) return hours === 1 ? "1 کاتژمێر" : `${hours} کاتژمێر`;
  return `${hours} کاتژمێر و ${minutes} خولەک`;
}

function planText(rule, when, eventLabel) {
  const rows = rule[when] || [];
  const items = rows.map((item) => durationText(item.hours, item.minutes));
  const word = when === "before" ? "پێش" : "دوای";
  const timed = items.length ? `ئاگادار دەکرێتەوە ${items.join("، ")} ${word} ${eventLabel}.` : "هیچ کاتێک دیاری نەکراوە.";
  if (rule.sendOnCreate) {
    return `دەستبەجێ لە کاتی تۆمارکردن دەنێردرێت. ${timed}`;
  }
  return timed;
}

const LANGS = [
  { key: "ku", label: "کوردی", dir: "rtl", title: "ناونیشان", body: "نامە", titlePh: "ناونیشانی کوردی", bodyPh: "دەقی کوردی" },
  { key: "ar", label: "عربي", dir: "rtl", title: "العنوان", body: "الرسالة", titlePh: "العنوان بالعربي", bodyPh: "نص الرسالة بالعربي" },
  { key: "en", label: "English", dir: "ltr", title: "Title", body: "Message", titlePh: "English title", bodyPh: "English message" },
];

function emptyDraft() {
  return {
    ku: { title: "", body: "" },
    ar: { title: "", body: "" },
    en: { title: "", body: "" },
  };
}

function SendForm({ draft, onChange }) {
  const [lang, setLang] = useState("ku");
  const current = LANGS.find((item) => item.key === lang);
  const values = draft[lang];

  return (
    <section className="notify-card">
      <Text className="notify-hint">
        سێ زمان بنووسە. کاتێک دەینێریت، هەر چارەخواز تەنها زمانی ئەپەکەی وەردەگرێت — ئینگلیزی تەنها ئینگلیزی، کوردی تەنها کوردی، عربی تەنها عربی.
      </Text>

      <Tabs
        activeKey={lang}
        onChange={setLang}
        className="notify-lang-tabs"
        items={LANGS.map((item) => ({ key: item.key, label: item.label }))}
      />

      <div className="notify-field" dir={current.dir}>
        <span>{current.title}</span>
        <Input
          size="large"
          className="notify-input"
          maxLength={80}
          placeholder={current.titlePh}
          value={values.title}
          onChange={(event) => onChange({ [lang]: { ...values, title: event.target.value } })}
        />
      </div>

      <div className="notify-field" dir={current.dir}>
        <span>{current.body}</span>
        <Input.TextArea
          className="notify-input notify-textarea"
          rows={5}
          maxLength={400}
          placeholder={current.bodyPh}
          value={values.body}
          onChange={(event) => onChange({ [lang]: { ...values, body: event.target.value } })}
        />
      </div>

      <div className="notify-send-previews">
        {LANGS.map((item) => {
          const text = draft[item.key];
          return (
            <div key={item.key} className="notify-send-preview" dir={item.dir}>
              <small>{item.label}</small>
              <strong>{text.title.trim() || item.titlePh}</strong>
              <p>{text.body.trim() || item.bodyPh}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function OffsetFields({ rows, when, eventLabel, disabled, onChange }) {
  const word = when === "before" ? "پێش" : "دوای";
  const updateRow = (index, partial) => {
    onChange(rows.map((item, i) => (i === index ? normalizeOffset({ ...item, ...partial }) : item)));
  };

  return (
    <div className={`notify-before${disabled ? " is-off" : ""}`}>
      <div className="notify-before-head">
        <span>کاتەکان {word} {eventLabel}</span>
        <Button
          className="patient-toolbar-btn"
          icon={<PlusOutlined />}
          disabled={disabled || rows.length >= 8}
          onClick={() => onChange([...rows, { hours: 1, minutes: 0 }])}
        >
          زیادکردنی کات
        </Button>
      </div>

      {rows.map((item, index) => (
        <div key={index} className="notify-before-row">
          <div className="notify-delay">
            <label>
              <small>کاتژمێر {word}</small>
              <InputNumber min={0} max={72} size="large" className="notify-input" value={item.hours} disabled={disabled} onChange={(hours) => updateRow(index, { hours })} />
            </label>
            <label>
              <small>خولەک {word}</small>
              <InputNumber min={0} max={59} size="large" className="notify-input" value={item.minutes} disabled={disabled} onChange={(minutes) => updateRow(index, { minutes })} />
            </label>
          </div>
          <Button
            type="text"
            danger
            className="notify-before-del"
            icon={<DeleteOutlined />}
            disabled={disabled || rows.length <= 1}
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
          />
        </div>
      ))}
    </div>
  );
}

function applyRules(raw) {
  const next = {};
  TABS.forEach(({ key }) => {
    next[key] = normalizeRule(key, raw?.[key]);
  });
  return next;
}

function Notify() {
  const { token } = theme.useToken();
  const [rules, setRules] = useState(loadRules);
  const [tab, setTab] = useState("send");
  const [draft, setDraft] = useState(emptyDraft);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const isSend = tab === "send";
  const current = isSend ? null : rules[tab];
  const meta = TABS.find((item) => item.key === tab);
  const rows = meta ? current[meta.when] || [] : [];

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}notify`)
      .then((res) => {
        const next = applyRules(res.data);
        setRules(next);
        localStorage.setItem(STORE_KEY, JSON.stringify(next));
      })
      .catch(() => {});
  }, []);

  const patch = (partial) => {
    setRules((prev) => ({
      ...prev,
      [tab]: normalizeRule(tab, { ...prev[tab], ...partial }),
    }));
  };

  const save = () => {
    setSaving(true);
    axios
      .put(`${API_BASE_URL}notify`, rules)
      .then((res) => {
        const next = applyRules(res.data);
        setRules(next);
        localStorage.setItem(STORE_KEY, JSON.stringify(next));
        message.success("پاشەکەوتکرا بۆ هەموو چارەخوازەکان");
      })
      .catch(() => {
        message.error("نەتوانرا پاشەکەوت بکرێت");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const sendGeneral = () => {
    const missing = LANGS.find((item) => !draft[item.key].title.trim() || !draft[item.key].body.trim());
    if (missing) {
      message.warning(`ناونیشان و نامە بۆ ${missing.label} پێویستن`);
      return;
    }
    setSending(true);
    axios
      .post(`${API_BASE_URL}notify/send`, draft)
      .then((res) => {
        const count = res.data?.targets || 0;
        if (!count) {
          message.warning("هیچ ئامێرێک ئامادە نییە");
          return;
        }
        setDraft(emptyDraft());
        message.success(`نێردرا بۆ ${count} چارەخواز. هەر کەسێک تەنها زمانی خۆی وەردەگرێت.`);
      })
      .catch(() => {
        message.error("نەتوانرا بنێردرێت");
      })
      .finally(() => {
        setSending(false);
      });
  };

  return (
    <div className="lab-page notify-page">
      <div className="patient-toolbar note-top notify-toolbar">
        <div className="note-top-head">
          <div>
            <Title level={3} style={{ color: token.colorPrimary, margin: 0 }}>
              ئاگادارکردنەوە
            </Title>
            <p className="note-top-sub">
              {isSend ? "نامەی گشتی بۆ هەموو چارەخوازەکان" : "ستاندارد بۆ هەموو چارەخوازەکان"}
            </p>
          </div>
        </div>
        <div className="patient-toolbar-end">
          {isSend ? (
            <Button type="primary" className="patient-toolbar-btn" icon={<SendOutlined />} loading={sending} onClick={sendGeneral}>
              ناردن
            </Button>
          ) : (
            <Button type="primary" className="patient-toolbar-btn" icon={<BellOutlined />} loading={saving} onClick={save}>
              پاشەکەوتکردن
            </Button>
          )}
        </div>
      </div>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        className="notify-tabs"
        items={[SEND_TAB, ...TABS].map((item) => ({
          key: item.key,
          label: item.label,
        }))}
      />

      {isSend ? (
        <SendForm draft={draft} onChange={(partial) => setDraft((prev) => ({ ...prev, ...partial }))} />
      ) : (
      <section className="notify-card">
        <Text className="notify-hint">{meta.hint}</Text>

        <label className="notify-switch">
          <span>چالاک بۆ هەموو چارەخوازەکان</span>
          <Switch checked={current.enabled} onChange={(enabled) => patch({ enabled })} />
        </label>

        {meta.when === "before" ? (
          <label className="notify-switch">
            <span>دەستبەجێ ئاگادارکردنەوە بنێرە کاتێک تۆمار دەکرێت</span>
            <Switch
              checked={Boolean(current.sendOnCreate)}
              disabled={!current.enabled}
              onChange={(sendOnCreate) => patch({ sendOnCreate })}
            />
          </label>
        ) : null}

        <OffsetFields
          rows={rows}
          when={meta.when}
          eventLabel={meta.eventLabel}
          disabled={!current.enabled}
          onChange={(nextRows) => patch({ [meta.when]: nextRows })}
        />

        {current.enabled ? <p className="notify-plan">{planText(current, meta.when, meta.eventLabel)}</p> : null}
      </section>
      )}
    </div>
  );
}

export default Notify;
